import { Badge, Flex } from '@mantine/core';
import { IconBell } from '@tabler/icons-react';
import Cookies from 'js-cookie';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import {
  BatchTypeEnum,
  Declaration,
  DeclarationCustomsFlowType,
  DeclarationCustomsType,
  DeclarationStatus,
  EnvironmentCountries,
  EORIGIN_DATE_FORMAT,
} from '@e-origin/shared';

import { copyIcon, sendIcon, viewIcon } from '@assets';
import { GoToDeclarationIcon, GoToDeclarationIconTabLocation, TableActions, TableColumnProps, TableNotificationIndicator } from '@components';
import { BE_GATE_MAX_DAYS } from '@constants';
import { DeclarationFlowBackgroundColors } from '@enums';
import { useSendBatchToBeGate } from '@hooks/batch';
import { useSendDeclarationsByLrn, useSendOneHv } from '@hooks/declaration';
import { useSettings } from '@hooks/useSettings';
import { STORAGE_KEYS } from '@utils';
import { setStoredCriteria } from '../../../../../criteria.caching';
import { countCustomsStateNotifications, getDuplicationModalTitle } from '../../../helpers';
import { declarationModalManager } from '../../declarationModalManager';
import { DeclarationStatusBadge } from './DeclarationStatusBadge';
import { TableCellInLineContent } from './TableCellInLineContent';

export const useDeclarationGroupTableColumns = ({ onSelectedRow }) => {
  const navigate = useNavigate();
  const { data: settings } = useSettings();
  const sendBatchToBeGateMutation = useSendBatchToBeGate();

  const useSendDeclarationsByLrnMutation = useSendDeclarationsByLrn();
  const sendOneHvMutation = useSendOneHv();

  const buildColumns = (): TableColumnProps<Declaration>[] => {
    return [
      ...([EnvironmentCountries.NL, EnvironmentCountries.FR].includes(settings.country)
        ? [
            {
              name: <IconBell />,
              cell: (row) => {
                const { actionsPending, notificationsPending } = countCustomsStateNotifications(row);

                return (
                  <TableNotificationIndicator
                    actionsPending={actionsPending}
                    notificationsPending={notificationsPending}
                    navigateTo={`/declarations/${row._id}/customs-state?source=declaration-groups&all-statuses=false`}
                  />
                );
              },
            },
          ]
        : []),
      {
        name: 'Batch Id',
        selector: 'messageInfo.batchId',
        cell: (row) => row.messageInfo.batchId,
        sortable: true,
      },
      {
        name: 'Batch',
        selector: 'declarationGlobal.batchName',
        sortable: true,
        cell: (row) => <TableCellInLineContent>{row.declarationGlobal?.batchName}</TableCellInLineContent>,
      },
      {
        name: 'Group',
        selector: 'generalInfo.group',
        cell: (row) => row.generalInfo?.group,
      },
      {
        name: 'Flow/System',
        cell: (row) => (
          <TableCellInLineContent>
            {row.customsState?.sentTo && (
              <Badge size="md" color={DeclarationFlowBackgroundColors[row.customsState?.flow ?? 'POST']}>
                {row.customsState?.flow?.toLowerCase() ?? ''}
                {row.customsState?.sentTo}
              </Badge>
            )}
          </TableCellInLineContent>
        ),
      },
      {
        name: 'Status',
        selector: 'generalInfo.status',
        sortable: true,
        cell: (row) => (
          <Flex gap={5}>
            <DeclarationStatusBadge status={row.generalInfo.status} />
            <GoToDeclarationIcon
              batchId={row.messageInfo.batch}
              source="declaration-groups"
              status={row.generalInfo.status}
              tab={
                row.generalInfo.status === DeclarationStatus.REJECTED ? GoToDeclarationIconTabLocation.CUSTOMS_STATE : GoToDeclarationIconTabLocation.DOCUMENTS
              }
            />
          </Flex>
        ),
      },
      {
        name: 'LRN',
        cell: (row) => row.messageInfo.LRN,
      },
      {
        name: 'Customer',
        selector: 'declarationGlobal.customerName',
        cell: (row) => <TableCellInLineContent>{row.declarationGlobal?.customerName}</TableCellInLineContent>,
        sortable: true,
      },
      {
        name: 'UCR',
        selector: 'consignment.referenceNumberUCR',
        cell: (row) => row.consignment.referenceNumberUCR,
        sortable: true,
      },
      ...(!settings?.isForwarder
        ? [
            {
              name: 'BE-GATE Days left',
              selector: 'customsState.begate.beGateSentDate',
              cell: (row) =>
                row.customsState?.begate?.beGateClearanceDate
                  ? Math.max(0, BE_GATE_MAX_DAYS - moment().utc().diff(moment(row.customsState.begate.beGateClearanceDate, EORIGIN_DATE_FORMAT), 'days'))
                  : undefined,
              sortable: true,
            },
            {
              name: 'BE-GATE Clearance Date',
              cell: (row) => row.customsState?.begate?.beGateClearanceDate,
            },
          ]
        : []),
      {
        name: 'MRN',
        selector: 'customsState.movementReference',
        cell: (row) => <TableCellInLineContent>{row.customsState?.movementReference}</TableCellInLineContent>,
        sortable: true,
      },
      {
        name: 'Release date',
        selector: 'customsState.releaseForImport.dateOfRelease',
        cell: (row) => <TableCellInLineContent>{row.customsState?.releaseForImport?.dateOfRelease}</TableCellInLineContent>,
        sortable: true,
      },
      {
        name: 'Exporter',
        selector: 'stakeholders.exporter.name',
        cell: (row) => row.stakeholders.exporter.name,
        sortable: true,
      },
      { name: 'Importer', cell: (row) => row.stakeholders.importer.name },
      { name: 'Importer Country', cell: (row) => row.stakeholders.importer.address.country },
      {
        name: 'Total packages',
        selector: 'declarationGlobal.totalPackages',
        cell: (row) => <TableCellInLineContent>{row.declarationGlobal?.totalPackages}</TableCellInLineContent>,
        sortable: true,
      },
      {
        name: 'Total Quantity',
        selector: 'declarationGlobal.totalQuantity',
        cell: (row) => <TableCellInLineContent>{row.declarationGlobal?.totalQuantity}</TableCellInLineContent>,
        sortable: true,
      },
      {
        name: 'Gross Mass',
        selector: 'declarationGlobal.grossMass',
        cell: (row) => <TableCellInLineContent>{row.declarationGlobal?.grossMass}</TableCellInLineContent>,
        sortable: true,
      },
      {
        name: 'Net Mass',
        selector: 'declarationGlobal.netMass',
        cell: (row) => <TableCellInLineContent>{row.declarationGlobal?.netMass}</TableCellInLineContent>,
        sortable: true,
      },
      {
        name: 'Number Articles',
        selector: 'declarationGlobal.numberOfArticles',
        cell: (row) => <TableCellInLineContent>{row.declarationGlobal?.numberOfArticles}</TableCellInLineContent>,
        sortable: true,
      },
      {
        name: 'Duties A00',
        cell: (row) => row.customsState?.calculationResults?.total?.find((item) => item.taxType === 'A00')?.payableTaxAmount,
      },
      {
        name: 'Duties 100',
        cell: (row) => row.customsState?.calculationResults?.total?.find((item) => item.taxType === '100')?.payableTaxAmount,
      },
      {
        name: 'Total VAT',
        cell: (row) => row.customsState?.calculationResults?.total?.find((item) => item.taxType === 'B00')?.payableTaxAmount,
      },
      {
        name: 'Actions',
        sticky: 'right',
        cell: (row) => (
          <TableActions
            actions={[
              {
                if: !settings?.isForwarder,
                icon: copyIcon,
                text: getDuplicationModalTitle(row),
                onClick: () => declarationModalManager.openDuplicateAndAmendment(row),
              },
              {
                icon: viewIcon,
                text: 'View declarations',
                onClick: () => {
                  setStoredCriteria(STORAGE_KEYS.COOKIES.BATCH_VIEW, row.generalInfo.isExport ? BatchTypeEnum.HIGH_VALUES_EXPORT : BatchTypeEnum.HIGH_VALUES);
                  navigate(`/declarations?lrn=${row.messageInfo.LRN}&tab=${row.generalInfo.status}`);
                },
              },
              {
                if:
                  (!settings?.isForwarder &&
                    [DeclarationStatus.NOT_SENT, DeclarationStatus.ANALYSIS, DeclarationStatus.BEGATE_NOT_RELEASED, DeclarationStatus.BEGATE_RELEASED].includes(
                      row.generalInfo.status,
                    )) ||
                  (settings.country === EnvironmentCountries.BE &&
                    row.generalInfo?.status === DeclarationStatus.SENT &&
                    Math.abs(moment.duration(moment().diff(moment(row.updatedAt))).asMinutes()) >= settings.reSendDelay),
                id: 'send-batch',
                icon: sendIcon,
                text: 'Send',
                onClick: () => declarationModalManager.openSendBatch(row),
              },
              {
                id: 'resend',
                if:
                  !settings?.isForwarder &&
                  ([DeclarationStatus.REJECTED, DeclarationStatus.BEGATE_SENT].includes(row.generalInfo.status) ||
                    (row.generalInfo.status === DeclarationStatus.ACCEPTED &&
                      [DeclarationCustomsType.IDMS, DeclarationCustomsType.DMS].includes(row.customsState?.sentTo as DeclarationCustomsType) &&
                      row.customsState?.flow === DeclarationCustomsFlowType.PRE)),
                icon: sendIcon,
                text: 'Send',
                onClick: () => {
                  if (row.customsState?.sentTo === DeclarationCustomsType.BE_GATE) {
                    sendBatchToBeGateMutation.mutate({ batchId: row.messageInfo.batch, lrn: row.messageInfo.LRN });
                    return;
                  }

                  if (
                    [DeclarationCustomsType.AES, DeclarationCustomsType.IDMS, DeclarationCustomsType.DMS, DeclarationCustomsType.NL_AES].includes(
                      row.customsState?.sentTo as DeclarationCustomsType,
                    )
                  ) {
                    if (!sendOneHvMutation.isPending) {
                      sendOneHvMutation.mutate({ lrn: row.messageInfo.LRN });
                    }
                    return;
                  }

                  if (!useSendDeclarationsByLrnMutation.isPending) {
                    useSendDeclarationsByLrnMutation.mutate({ batchId: row.messageInfo.batch, lrns: [row.messageInfo.LRN] });
                  }
                },
              },
            ]}
            secondaryActions={[]}
            onClick={() => onSelectedRow(row)}
          />
        ),
      },
    ];
  };

  return buildColumns();
};
